import { useEffect, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { getAbout, updateAbout } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

const AdminAbout = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: 'About Us',
    subtitle: '',
    content: '',
    image_data: ''
  });

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const res = await getAbout();
      setFormData({
        title: res.data.title || 'About Us',
        subtitle: res.data.subtitle || '',
        content: res.data.content || '',
        image_data: res.data.image_data || ''
      });
    } catch (error) {
      toast.error('Failed to load about content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, image_data: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await updateAbout(formData);
      toast.success('About content updated successfully');
    } catch (error) {
      toast.error('Failed to update about content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-about-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            About Page
          </h1>
          <p className="text-neutral-500 mt-2">Edit your about page content</p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-3xl">
          <div className="admin-card space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Page Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="About Us"
                data-testid="about-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtitle">Subtitle</Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                placeholder="A brief tagline or subtitle"
                data-testid="about-subtitle-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Tell visitors about yourself, your experience, and what makes you unique..."
                rows={10}
                data-testid="about-content-input"
              />
              <p className="text-xs text-neutral-500">
                You can use line breaks to create paragraphs
              </p>
            </div>

            <div className="space-y-2">
              <Label>About Image</Label>
              <div className="border-2 border-dashed border-neutral-200 rounded-lg p-6">
                {formData.image_data ? (
                  <div className="relative">
                    <img 
                      src={formData.image_data} 
                      alt="About preview"
                      className="max-h-64 mx-auto rounded"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData(prev => ({ ...prev, image_data: '' }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block text-center">
                    <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                    <p className="text-sm text-neutral-500">Click to upload image</p>
                    <p className="text-xs text-neutral-400 mt-1">Max 5MB</p>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileUpload}
                      data-testid="about-image-input"
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button 
                type="submit" 
                className="bg-neutral-900 hover:bg-neutral-800"
                disabled={saving}
                data-testid="save-about-btn"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminAbout;
